import { cn } from '@/lib/utils';

import { NOTIFICATION_ICONS, type NotificationIconName } from '../notification-icons';
import { getNotificationVisual } from '../notification-visuals';

interface CommonProps {
    /** Sizing/colour utilities, e.g. `size-5`. Colour arrives via `currentColor`. */
    className?: string;
}

/**
 * Either name a glyph outright (the bell, a close X — chrome that has no
 * category), or hand over a notification's category and let the visual map
 * decide. The two are mutually exclusive so a caller cannot pass both and leave
 * the reader guessing which one wins.
 */
type NotificationIconProps = CommonProps &
    (
        | { name: NotificationIconName; category?: never; metadata?: never }
        | {
              category: string;
              metadata?: Record<string, unknown> | null;
              name?: never;
          }
    );

/**
 * Draws one notification glyph.
 *
 * The glyph is a Lucide component looked up in `NOTIFICATION_ICONS` — the same
 * icon family the rest of the app uses. Resolving through a name keeps
 * `notification-visuals` a plain data table with no icon imports of its own,
 * which is the reason this indirection exists at all.
 *
 * Colour is inherited: Lucide strokes with `currentColor`, and the chip wrapper
 * already carries `TONE_ICON_CLASSES` (which sets a `text-*` colour), so the
 * tone system keeps working with nothing passed down.
 *
 * `aria-hidden` is unconditional. Every one of these sits next to text that
 * already says what the notification is, so announcing the glyph too would just
 * make a screen reader repeat itself.
 */
export function NotificationIcon({ className, ...props }: NotificationIconProps) {
    const name: NotificationIconName =
        props.name ?? getNotificationVisual(props.category, props.metadata).icon;
    const Icon = NOTIFICATION_ICONS[name];

    return <Icon aria-hidden focusable="false" className={cn('shrink-0', className)} />;
}
